// src/pages/Transactions.tsx
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/store";
import { getTransactions } from "../redux/slices/transactionSlice";
import { GlobalStyles } from "../styles/globalStyles";
import {
  Search,
  ArrowDownRight,
  ArrowUpRight,
  Clock,
  CheckCircle,
  XCircle,
  RefreshCw,
  Filter,
  Sparkles,
  ChevronDown,
} from "lucide-react";
import toast from "react-hot-toast";
import { formatFiat } from "../utils/currency";

const Transactions: React.FC = () => {
  const dispatch = useDispatch();
  const { transactions, isLoading } = useSelector(
    (state: RootState) => state.transactions,
  );
  const [filter, setFilter] = useState({
    type: "",
    status: "",
    search: "",
  });

  useEffect(() => {
    dispatch(getTransactions() as any);
  }, [dispatch]);

  const filteredTransactions = transactions.filter((transaction) => {
    if (filter.type && transaction.type !== filter.type) return false;
    if (filter.status && transaction.status !== filter.status) return false;
    if (filter.search) {
      const searchLower = filter.search.toLowerCase();
      return (
        transaction.tx_hash?.toLowerCase().includes(searchLower) ||
        transaction.wallet_address?.toLowerCase().includes(searchLower) ||
        transaction.type.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  const handleRefresh = () => {
    dispatch(getTransactions() as any);
    toast.success("Transactions refreshed");
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "confirmed":
        return <CheckCircle className="h-3 w-3 text-green-500" />;
      case "pending":
        return <Clock className="h-3 w-3 text-yellow-500 animate-pulse" />;
      case "rejected":
        return <XCircle className="h-3 w-3 text-red-500" />;
      default:
        return <Clock className="h-3 w-3 text-gray-400" />;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent rounded-3xl blur-xl opacity-70"></div>
            <div className="relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/30 dark:border-gray-700/30 p-12">
              <div className="relative">
                <div className="w-16 h-16 rounded-full border-4 border-primary/20 border-t-primary animate-spin mx-auto"></div>
                <Sparkles className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 h-6 w-6 text-primary" />
              </div>
              <p className="mt-6 text-gray-600 dark:text-gray-400 font-medium">
                Loading transactions...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-gray-200 dark:from-gray-900 dark:via-gray-800 dark:to-gray-950">
      <div className="p-4 lg:p-5">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <h1 className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent">
                  Transactions
                </h1>
                <Sparkles className="h-4 w-4 text-primary/60" />
              </div>
              <p className="text-gray-500 dark:text-gray-400 text-sm">
                Track all your transaction history
              </p>
            </div>
            <button
              onClick={handleRefresh}
              className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonMedium} group/refresh`}
            >
              <RefreshCw className="h-4 w-4 mr-2 transition-transform group-hover/refresh:rotate-180 duration-500" />
              <span>Refresh</span>
            </button>
          </div>
        </div>
        {/* Filters */}
        <div className={`${GlobalStyles.card} mb-6 backdrop-blur-xl`}>
          <div className="flex items-center gap-2 mb-4">
            <Filter className="h-4 w-4 text-primary" />
            <h3 className="font-semibold text-gray-700 dark:text-gray-300">
              Filter Transactions
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="group">
              <label
                className={`${GlobalStyles.inputLabel} flex items-center gap-1`}
              >
                <span>Type</span>
                <Sparkles className="h-3 w-3 text-primary/60" />
              </label>
              <div className="relative">
                <select
                  value={filter.type}
                  onChange={(e) =>
                    setFilter({ ...filter, type: e.target.value })
                  }
                  className={`${GlobalStyles.input} appearance-none pr-8`}
                >
                  <option value="">All Types</option>
                  <option value="deposit">Deposit</option>
                  <option value="withdrawal">Withdrawal</option>
                  <option value="bonus">Bonus</option>
                </select>
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                </div>
              </div>
            </div>

            <div className="group">
              <label
                className={`${GlobalStyles.inputLabel} flex items-center gap-1`}
              >
                <span>Status</span>
              </label>
              <div className="relative">
                <select
                  value={filter.status}
                  onChange={(e) =>
                    setFilter({ ...filter, status: e.target.value })
                  }
                  className={`${GlobalStyles.input} appearance-none pr-8`}
                >
                  <option value="">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="rejected">Rejected</option>
                </select>
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                </div>
              </div>
            </div>

            <div className="group md:col-span-2">
              <label
                className={`${GlobalStyles.inputLabel} flex items-center gap-1`}
              >
                <span>Search</span>
                <Search className="h-3 w-3 text-primary/60" />
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 group-focus-within:text-primary transition-colors" />
                <input
                  type="text"
                  placeholder="Search by hash, wallet, or type..."
                  value={filter.search}
                  onChange={(e) =>
                    setFilter({ ...filter, search: e.target.value })
                  }
                  className={`${GlobalStyles.input} pl-10`}
                />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-gray-200/30 dark:border-gray-700/30 mt-4">
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 rounded-full bg-primary animate-pulse"></div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                <span className="font-medium text-gray-700 dark:text-gray-300">
                  {filteredTransactions.length}
                </span>{" "}
                transactions found
              </p>
            </div>
            <button
              onClick={() => setFilter({ type: "", status: "", search: "" })}
              className={`${GlobalStyles.button} ${GlobalStyles.buttonGhost} ${GlobalStyles.buttonSmall} group/clear`}
            >
              <span>Clear Filters</span>
              <Sparkles className="h-3 w-3 ml-1 opacity-0 group-hover/clear:opacity-100 transition-opacity" />
            </button>
          </div>
        </div>
        {/* Transactions Table */}
        <div
          className={`${GlobalStyles.card} backdrop-blur-xl overflow-hidden`}
        >
          <div className="overflow-x-auto rounded-xl">
            <table className={GlobalStyles.table}>
              <thead>
                <tr className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800/50 dark:to-gray-900/50 backdrop-blur-sm">
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Transaction Hash
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Date & Time
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200/30 dark:divide-gray-700/30">
                {filteredTransactions.length > 0 ? (
                  filteredTransactions.map((transaction) => (
                    <tr
                      key={transaction.id}
                      className="hover:bg-gray-50/50 dark:hover:bg-gray-800/30 transition-all duration-200 group/row"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div
                            className={`p-2 rounded-lg ${
                              transaction.type === "deposit"
                                ? "bg-green-500/10"
                                : transaction.type === "withdrawal"
                                  ? "bg-red-500/10"
                                  : "bg-purple-500/10"
                            }`}
                          >
                            {transaction.type === "deposit" ? (
                              <ArrowDownRight className="h-4 w-4 text-green-500" />
                            ) : (
                              <ArrowUpRight className="h-4 w-4 text-red-500" />
                            )}
                          </div>
                          <span className="ml-3 font-medium capitalize text-gray-900 dark:text-white">
                            {transaction.type}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div
                          className={`text-lg font-semibold ${
                            transaction.type === "deposit"
                              ? "text-green-600 dark:text-green-400"
                              : transaction.type === "withdrawal"
                                ? "text-red-600 dark:text-red-400"
                                : "text-purple-600 dark:text-purple-400"
                          }`}
                        >
                          {transaction.type === "deposit" ? "+" : "-"}
                          {formatFiat(transaction.amount)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {getStatusIcon(transaction.status)}
                          <span
                            className={`ml-2 text-xs font-medium px-2.5 py-1 rounded-full ${
                              transaction.status === "confirmed"
                                ? "bg-green-500/10 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800/50"
                                : transaction.status === "pending"
                                  ? "bg-yellow-500/10 text-yellow-700 dark:text-yellow-300 border border-yellow-200 dark:border-yellow-800/50"
                                  : "bg-red-500/10 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800/50"
                            }`}
                          >
                            {transaction.status}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div
                          className="font-mono text-sm truncate max-w-xs text-gray-600 dark:text-gray-400 group-hover/row:text-gray-800 dark:group-hover/row:text-gray-200 transition-colors"
                          title={transaction.tx_hash || "N/A"}
                        >
                          {transaction.tx_hash ? (
                            <>
                              <span className="text-primary/60">
                                {transaction.tx_hash.slice(0, 16)}...
                              </span>
                              <span className="text-gray-400 dark:text-gray-500">
                                {transaction.tx_hash.slice(-16)}
                              </span>
                            </>
                          ) : (
                            "N/A"
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col">
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            {new Date(
                              transaction.created_at,
                            ).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-500">
                            {new Date(
                              transaction.created_at,
                            ).toLocaleTimeString("en-US", {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="px-6 py-12">
                      <div className="text-center">
                        <div className="relative mx-auto w-20 h-20 mb-4">
                          <div className="absolute inset-0 bg-gradient-to-r from-primary/10 to-primary/5 rounded-full blur-lg"></div>
                          <div className="relative w-20 h-20 bg-white/50 dark:bg-gray-800/50 rounded-full flex items-center justify-center backdrop-blur-sm border border-gray-200/30 dark:border-gray-700/30">
                            <Search className="h-8 w-8 text-gray-400" />
                          </div>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                          No transactions found
                        </h3>
                        <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
                          {transactions.length === 0
                            ? "Start by making your first transaction to see it here"
                            : "Try adjusting your filters or search terms"}
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Transactions;
